import React, { useState, useEffect, useRef } from 'react';
import { AdvisorSupportEmail } from '../../data/advisorSupportEmails';
import { useChats } from '../../contexts/ChatsContext';
import { useAuth } from '../../contexts/AuthContext';
import { chatWithAgent } from '../../services/agentService';

interface Props {
  email: AdvisorSupportEmail;
  sessionId: string;
}

const STEPS = [
  { name: 'Parse',   dur: 7000 },
  { name: 'Lookup',  dur: 9000 },
  { name: 'Diff',    dur: 0 },
  { name: 'Commit',  dur: 11000 },
];

function toolBadge(text: string) {
  return `<div class="panel-bubble-tool"><span class="tool-tag">TOOL</span>${text}</div>`;
}
function doneBadge(text: string) {
  return `<span class="step-done" style="display:inline-block;background:#ECFDF3;color:#166534;font-size:10.5px;padding:1px 6px;border-radius:3px;margin-right:5px;font-weight:500">✓</span>${text}`;
}
function systemBadge(name: string) {
  const color = '#38BDF8';
  const icon = `<svg width="11" height="11" viewBox="0 0 14 14" fill="none" style="flex-shrink:0;margin-top:1px"><ellipse cx="7" cy="4" rx="5" ry="2" stroke="${color}" stroke-width="1.4"/><path d="M2 4v6c0 1.1 2.24 2 5 2s5-.9 5-2V4" stroke="${color}" stroke-width="1.4"/><path d="M2 7c0 1.1 2.24 2 5 2s5-.9 5-2" stroke="${color}" stroke-width="1.4"/></svg>`;
  return `<span style="display:inline-flex;align-items:center;gap:5px;background:#0F172A;border:1px solid rgba(255,255,255,0.09);border-radius:20px;padding:3px 9px 3px 7px;font-size:11px;vertical-align:middle;margin:0 3px;line-height:1.2">${icon}<span style="color:${color};font-weight:700;font-family:monospace;letter-spacing:0.01em">${name}</span><span style="background:rgba(255,255,255,0.06);color:#64748B;font-size:9px;padding:1px 5px;border-radius:4px;font-family:monospace;font-weight:500;margin-left:2px">MCP</span></span>`;
}

function makeStepMsgs(email: AdvisorSupportEmail) {
  const subjectHasAccount = /\d{4}-\d{4}/.test(email.subject);
  const instructionLabel = email.instructionType === 'replace' ? 'find-and-replace' : 'full overwrite';
  return [
    // Step 0: Parse
    [
      { delay: 200,  content: `Parsing incoming email from <strong>${email.from}</strong>…` },
      { delay: 1800, content: toolBadge('Scanning subject line for account number pattern (\\d{4}-\\d{4})') },
      { delay: 3400, content: subjectHasAccount
          ? `Account number <strong>${email.accountNumber}</strong> found in subject line`
          : `No account number in subject — scanning body…` },
      { delay: 4800, content: subjectHasAccount
          ? toolBadge(`Client name extracted from subject: <strong>[${email.clientName}]</strong>`)
          : `Account number <strong>${email.accountNumber}</strong> found in body · client: <strong>${email.clientName}</strong>` },
      { delay: 6000, content: doneBadge(`Parse complete · account <strong>${email.accountNumber}</strong> · instruction: <strong>${instructionLabel}</strong>`) },
    ],
    // Step 1: Lookup
    [
      { delay: 200,  content: `Calling ${systemBadge('Account Mgmt')} via MCP gateway` },
      { delay: 2200, content: toolBadge(`SELECT registration_name FROM accounts WHERE account_id = '${email.accountNumber}'`) },
      { delay: 4200, content: `Account found · client: <strong>${email.clientName}</strong>` },
      { delay: 5800, content: `Current registration: <em>"${email.currentRegistration}"</em>` },
      { delay: 7800, content: doneBadge('Registration fetched · computing proposed change…') },
    ],
    // Step 2: Diff — no chat messages, human gate shown in panel
    [],
    // Step 3: Commit (runs after approval)
    [
      { delay: 200,  content: `Writing updated registration to ${systemBadge('Account Mgmt')}…` },
      { delay: 2000, content: toolBadge(`UPDATE accounts SET registration_name = '${email.proposedRegistration}' WHERE account_id = '${email.accountNumber}'`) },
      { delay: 4000, content: doneBadge('Registration updated · audit log written') },
      { delay: 5500, content: `Drafting confirmation email to <strong>${email.from}</strong>…` },
    ],
  ];
}

function StepProgress({ current, viewing, onStepClick }: {
  current: number;
  viewing: number;
  onStepClick: (i: number) => void;
}) {
  return (
    <div className="px-5 pt-4 pb-4 border-b border-trim">
      <div className="text-[10px] font-semibold text-muted uppercase tracking-widest mb-4">
        Processing Pipeline
      </div>
      <div className="flex items-start">
        {STEPS.map((s, i) => {
          const isClickable = i <= current;
          const isViewing = i === viewing && i !== current;
          return (
            <React.Fragment key={s.name}>
              <div
                className={`flex flex-col items-center ${isClickable ? 'cursor-pointer' : 'cursor-default'}`}
                style={{ width: 72, flexShrink: 0 }}
                onClick={() => isClickable && onStepClick(i)}
              >
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold transition-all ${
                  isViewing ? 'ring-2 ring-offset-1 ring-teal-400' : ''
                } ${
                  i < current
                    ? 'bg-green-500 text-white hover:opacity-80'
                    : i === current
                      ? 'bg-teal-500 text-white shadow-sm'
                      : 'bg-wash text-muted border border-trim'
                }`}>
                  {i < current ? (
                    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                      <path d="M2 7l3.5 3.5L12 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : (
                    String(i + 1)
                  )}
                </div>
                <div className={`text-[11px] mt-1.5 text-center leading-tight px-0.5 ${
                  isViewing ? 'text-teal-700 font-semibold' :
                  i < current ? 'text-green-700 font-medium' :
                  i === current ? 'text-ink font-semibold' : 'text-muted'
                }`}>
                  {s.name}
                </div>
              </div>
              {i < STEPS.length - 1 && (
                <div
                  className={`flex-1 h-px transition-colors ${i < current ? 'bg-green-400' : 'bg-trim'}`}
                  style={{ marginTop: 14 }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function GovernanceStrip({ text }: { text: string }) {
  return (
    <div className="mx-4 mt-3 mb-1 flex items-start gap-2 bg-wash border border-trim rounded-lg px-3 py-2">
      <svg width="13" height="13" viewBox="0 0 14 14" fill="none" className="flex-shrink-0 mt-0.5 text-muted">
        <path d="M7 1L2 4v3c0 3 2.5 5.5 5 6 2.5-.5 5-3 5-6V4L7 1z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
      </svg>
      <span className="text-xs text-muted leading-snug">{text}</span>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-xs font-semibold text-orange-700 uppercase tracking-widest bg-orange-50 px-4 py-2">
      {children}
    </div>
  );
}

function Step0Artifact({ email }: { email: AdvisorSupportEmail }) {
  const subjectHasAccount = /\d{4}-\d{4}/.test(email.subject);
  return (
    <div>
      <SectionLabel>Incoming email</SectionLabel>
      <div className="mx-4 my-3 border border-trim rounded-xl overflow-hidden">
        <div className="px-4 py-3 bg-wash border-b border-trim">
          <div className="text-xs font-semibold text-ink">{email.from} &lt;{email.fromEmail}&gt;</div>
          <div className="text-xs text-muted mt-0.5">To: {email.to} · {email.date}</div>
        </div>
        <div className="px-4 py-3">
          <div className="text-sm font-semibold text-ink mb-2">{email.subject}</div>
          <div className="text-sm text-copy leading-relaxed whitespace-pre-line">{email.body}</div>
        </div>
      </div>
      <div className="mx-4 my-3 grid grid-cols-2 gap-2">
        <div className="bg-wash border border-trim rounded-xl px-3 py-2.5">
          <div className="text-xs text-muted">Account</div>
          <div className="text-sm font-semibold text-ink mt-0.5">{email.accountNumber}</div>
        </div>
        <div className="bg-wash border border-trim rounded-xl px-3 py-2.5">
          <div className="text-xs text-muted">Source</div>
          <div className="text-sm font-semibold text-ink mt-0.5">{subjectHasAccount ? 'Subject line' : 'Email body'}</div>
        </div>
        <div className="col-span-2 bg-wash border border-trim rounded-xl px-3 py-2.5">
          <div className="text-xs text-muted">Instruction type</div>
          <div className="text-sm font-semibold text-ink mt-0.5 capitalize">{email.instructionType === 'replace' ? 'Find-and-replace' : 'Full overwrite'}</div>
        </div>
      </div>
      <GovernanceStrip text="ace.governance · session opened · advisor identity verified · entitlements checked" />
    </div>
  );
}

function Step1Artifact({ email }: { email: AdvisorSupportEmail }) {
  return (
    <div>
      <div className="mx-4 mt-3 flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-1">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="text-green-600 flex-shrink-0">
          <path d="M2 7l3.5 3.5L12 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span className="text-xs font-semibold text-green-700">Account found · registration fetched</span>
      </div>
      <SectionLabel>Account details</SectionLabel>
      <div className="mx-4 my-3 border border-trim rounded-xl overflow-hidden">
        {[
          { label: 'Account number', val: email.accountNumber },
          { label: 'Client name',    val: email.clientName },
          { label: 'Advisor',        val: email.from },
        ].map(({ label, val }) => (
          <div key={label} className="flex items-start gap-3 px-4 py-3 border-b border-trim last:border-b-0">
            <div className="text-xs text-muted w-28 flex-shrink-0 pt-0.5">{label}</div>
            <div className="text-xs font-medium text-ink">{val}</div>
          </div>
        ))}
        <div className="px-4 py-3 bg-amber-50 border-t border-amber-100">
          <div className="text-xs text-muted mb-1">Current registration</div>
          <div className="text-sm font-mono font-semibold text-amber-900 bg-amber-100 px-2 py-1 rounded">
            {email.currentRegistration}
          </div>
        </div>
      </div>
      <GovernanceStrip text="Account Mgmt MCP · registration_name field · read-only · query logged" />
    </div>
  );
}

function Step2Artifact({ email, approved, canApprove, onApprove, onReject }: {
  email: AdvisorSupportEmail;
  approved: boolean;
  canApprove: boolean;
  onApprove: () => void;
  onReject: () => void;
}) {
  if (approved) {
    return (
      <div className="mx-4 my-4 flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-4">
        <svg width="18" height="18" viewBox="0 0 14 14" fill="none" className="text-green-600 flex-shrink-0">
          <path d="M2 7l3.5 3.5L12 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <div>
          <div className="text-sm font-semibold text-green-800">Change approved · committing…</div>
          <div className="text-xs text-green-700 mt-0.5">{email.accountNumber} · {email.clientName}</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <SectionLabel>Registration diff — awaiting approval</SectionLabel>
      <div className="mx-4 my-3 border border-trim rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-trim">
          <div className="text-xs text-muted mb-1.5">Before</div>
          <div className="font-mono text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-red-800 leading-relaxed">
            {email.currentRegistration}
          </div>
        </div>
        <div className="px-4 py-3">
          <div className="text-xs text-muted mb-1.5">After</div>
          <div className="font-mono text-sm bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-green-800 leading-relaxed">
            {email.proposedRegistration}
          </div>
        </div>
      </div>
      <div className="mx-4 mb-3 flex items-center gap-2">
        <div className="flex items-center gap-1.5 text-xs text-muted">
          <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
            <path d="M7 1L2 4v3c0 3 2.5 5.5 5 6 2.5-.5 5-3 5-6V4L7 1z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
          </svg>
          Human approval required before write
        </div>
      </div>
      {canApprove ? (
        <div className="mx-4 flex gap-2 mb-4">
          <button
            onClick={onApprove}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-green-500 text-white text-sm font-medium hover:bg-green-600 transition-colors"
          >
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
              <path d="M2 7l3.5 3.5L12 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Approve change
          </button>
          <button
            onClick={onReject}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-trim text-sm font-medium text-copy hover:bg-wash transition-colors"
          >
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
              <path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            Reject
          </button>
        </div>
      ) : (
        <div className="mx-4 mb-4 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
          Approving this change requires a Team Manager — your role can prepare but not approve.
        </div>
      )}
      <GovernanceStrip text="ace.governance · 2-step write policy · operator approval required · change will be audit-logged" />
    </div>
  );
}

function Step3Artifact({ email, draftText, committed, onDraftChange }: {
  email: AdvisorSupportEmail;
  draftText: string;
  committed: boolean;
  onDraftChange: (t: string) => void;
}) {
  if (!committed && !draftText) {
    return (
      <div className="mx-4 my-4">
        <div className="flex items-center gap-2 px-4 py-6 text-sm text-muted">
          <div className="w-4 h-4 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
          Committing change and drafting reply…
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 pb-4">
      <div className="mx-4 mt-3 flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-1">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="text-green-600 flex-shrink-0">
          <path d="M2 7l3.5 3.5L12 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span className="text-xs font-semibold text-green-700">
          {email.accountNumber} updated · {email.proposedRegistration}
        </span>
      </div>
      <SectionLabel>Draft reply — review before sending</SectionLabel>
      <div className="mx-4 my-3 flex-1 flex min-h-0">
        <textarea
          className="w-full h-full bg-white border border-trim rounded-xl px-4 py-3 text-sm text-ink leading-relaxed resize-none outline-none focus:border-teal-400 transition-colors font-[inherit]"
          style={{ minHeight: 220 }}
          value={draftText}
          onChange={e => onDraftChange(e.target.value)}
        />
      </div>
      <GovernanceStrip text="Reasoning trace · change committed · reply drafted · awaiting advisor send" />
    </div>
  );
}

export function AdvisorRegistrationPanel({ email, sessionId }: Props) {
  const { sessions, appendMsg, updateSession } = useChats();
  const { can } = useAuth();
  const session = sessions.find(s => s.id === sessionId);
  const alreadyComplete  = session?.advisorWorkflowComplete === true;
  const savedDraft       = session?.advisorDraftText || '';
  const savedApproved    = session?.advisorApproved === true;

  const [step, setStep]           = useState<number>(alreadyComplete ? 3 : -1);
  const [viewStep, setViewStep]   = useState<number | null>(null);
  const [diffApproved, setDiffApproved] = useState(savedApproved);
  const [draftText, setDraftText] = useState(savedDraft);
  const [committed, setCommitted] = useState(alreadyComplete);
  const [showSentModal, setShowSentModal] = useState(false);

  const startedRef       = useRef(false);
  const appendMsgRef     = useRef(appendMsg);
  const sessionIdRef     = useRef(sessionId);
  const updateSessionRef = useRef(updateSession);
  appendMsgRef.current     = appendMsg;
  sessionIdRef.current     = sessionId;
  updateSessionRef.current = updateSession;

  const now = () => new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const stepMsgs = makeStepMsgs(email);

  useEffect(() => {
    if (startedRef.current || alreadyComplete) return;
    startedRef.current = true;
    setStep(0);
  }, [alreadyComplete]);

  useEffect(() => {
    if (step < 0 || step === 2) return;
    const timers: ReturnType<typeof setTimeout>[] = [];

    stepMsgs[step]?.forEach(({ delay, content }) => {
      timers.push(setTimeout(() => {
        appendMsgRef.current(sessionIdRef.current, {
          id: String(Date.now() + Math.random()),
          role: 'agent',
          content,
          time: now(),
          trace: !content.includes('step-done'),
        });
      }, delay));
    });

    // Auto-advance steps 0 and 1; step 2 waits for human approval; step 3 triggers Claude
    if (step < 2) {
      timers.push(setTimeout(() => setStep(s => s + 1), STEPS[step].dur));
    }

    if (step === 3) {
      timers.push(setTimeout(async () => {
        try {
          const resp = await chatWithAgent(
            `Draft a short, professional confirmation email back to the advisor for this registration name change:\n\nAdvisor: ${email.from} <${email.fromEmail}>\nAccount: ${email.accountNumber}\nClient: ${email.clientName}\nChange: Registration updated from "${email.currentRegistration}" to "${email.proposedRegistration}"\n\nKeep it to 3–4 sentences: confirm the change was made, state the new registration name, and let them know it is effective immediately. Address the advisor by first name.`,
            'advisor-support',
            []
          );
          const draft = resp.message;
          setDraftText(draft);
          setCommitted(true);
          updateSessionRef.current(sessionIdRef.current, {
            advisorDraftText: draft,
            advisorWorkflowComplete: true,
          });
          appendMsgRef.current(sessionIdRef.current, {
            id: String(Date.now() + Math.random()),
            role: 'agent',
            content: doneBadge('Change committed · draft reply ready for review.'),
            time: now(),
          });
        } catch {
          const firstName = email.from.split(' ')[0];
          const fallback = `Hi ${firstName} —\n\nThe registration for account ${email.accountNumber} (${email.clientName}) has been updated as requested.\n\nNew registration: ${email.proposedRegistration}\n\nThe change is effective immediately. Please let us know if you need anything else.`;
          setDraftText(fallback);
          setCommitted(true);
          updateSessionRef.current(sessionIdRef.current, {
            advisorDraftText: fallback,
            advisorWorkflowComplete: true,
          });
          appendMsgRef.current(sessionIdRef.current, {
            id: String(Date.now() + Math.random()),
            role: 'agent',
            content: doneBadge('Change committed · draft reply ready for review.'),
            time: now(),
          });
        }
      }, 3000));
    }

    return () => timers.forEach(clearTimeout);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  function handleApprove() {
    setDiffApproved(true);
    updateSession(sessionId, { advisorApproved: true });
    appendMsg(sessionId, {
      id: String(Date.now() + Math.random()),
      role: 'agent',
      content: doneBadge(`Registration change <strong>approved</strong> for ${email.accountNumber} · committing to Account Management System…`),
      time: now(),
    });
    setStep(3);
  }

  function handleReject() {
    appendMsg(sessionId, {
      id: String(Date.now() + Math.random()),
      role: 'agent',
      content: 'Registration change rejected. Please revise the instruction or route to a supervisor.',
      time: now(),
    });
  }

  const actualStep  = step < 0 ? 0 : step;
  const displayStep = viewStep !== null ? viewStep : actualStep;

  function handleStepClick(i: number) {
    setViewStep(i === actualStep ? null : i);
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      <div className="px-5 pt-4 pb-3 border-b border-trim">
        <div className="text-sm font-semibold text-ink">{email.accountNumber} — {email.clientName}</div>
        <div className="text-xs text-muted mt-0.5">From: {email.from} · {email.date}</div>
      </div>

      <StepProgress current={actualStep} viewing={displayStep} onStepClick={handleStepClick} />

      <div className="flex-1 flex flex-col min-h-0">
        {displayStep === 0 && <Step0Artifact email={email} />}
        {displayStep === 1 && <Step1Artifact email={email} />}
        {displayStep === 2 && (
          <Step2Artifact
            email={email}
            approved={diffApproved}
            canApprove={can('draft.approve')}
            onApprove={handleApprove}
            onReject={handleReject}
          />
        )}
        {displayStep === 3 && (
          <Step3Artifact
            email={email}
            draftText={draftText}
            committed={committed}
            onDraftChange={text => {
              setDraftText(text);
              updateSessionRef.current(sessionIdRef.current, { advisorDraftText: text });
            }}
          />
        )}
      </div>

      {showSentModal && (
        <div
          className="fixed inset-0 z-[400] flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.3)' }}
          onClick={() => setShowSentModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl px-8 py-7 flex flex-col items-center gap-4 max-w-xs w-full mx-4"
            onClick={e => e.stopPropagation()}
          >
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                <path d="M6 16l7 7L26 9" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="text-center">
              <div className="text-lg font-semibold text-ink">Reply sent</div>
              <div className="text-sm text-muted mt-1.5">
                Confirmation delivered to <strong className="text-ink">{email.from}</strong>
              </div>
              <div className="text-xs text-muted mt-1">{email.accountNumber} · registration updated · session archived</div>
            </div>
            <button
              onClick={() => setShowSentModal(false)}
              className="w-full py-2.5 rounded-xl bg-green-500 text-white text-sm font-semibold hover:bg-green-600 transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
